/**
 * ModalValidator.js
 * -------------------------------
 * ES6 Module for validating the ContactNamespace and ensuring modal readiness.
 *
 * Responsibilities:
 * - Validate that the ContactNamespace instance exists and exposes required methods
 * - Generate structured validation results with issues and status
 * - Track critical validation errors using ErrorHandler
 *
 * Features:
 * - Configurable required methods (defaults: openModal, healthCheck)
 * - Separate structural checks from functional readiness
 * - Provides diagnostic methods: getRecentErrors, hasCriticalIssues, getErrorStats, getDebugInfo
 * - Graceful fallback when ErrorHandler is unavailable
 *
 * Limitations:
 * - Does not simulate UI interactions or invoke modal behavior
 * - Error tracking is minimal to avoid code bloat
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

export default class ModalValidator {
  constructor(namespace) {
    this.namespace = namespace;
    this.requiredMethods = ['openModal', 'healthCheck'];
    this._initializeErrorHandler();
  }

  /**
   * Initialize or fallback the error handler
   * @private
   */
  _initializeErrorHandler() {
    try {
      this._errorHandler = new ErrorHandler({ module: 'ModalValidator', maxErrors: 8 });
    } catch {
      // Fallback no-op handler
      this._errorHandler = {
        handleError:       (msg, error, ctx) => console.error(`ModalValidator Error: ${msg}`, { error, ctx }),
        handleFatalError:  (msg, error, ctx) => console.error(`ModalValidator FATAL: ${msg}`, { error, ctx }),
        getRecentErrors:   () => [],
        hasFatalErrors:    () => false,
        getErrorStats:     () => ({ totalErrors: 0, fatalErrors: 0 })
      };
    }
  }

  /**
   * Validates required methods on the namespace
   * @returns {Promise<{valid: boolean, issues: string[]}>}
   */
  async validateNamespace() {
    const validation = { valid: true, issues: [] };
    if (!this.namespace) {
      validation.valid = false;
      validation.issues.push('Namespace is null or undefined');
      this._errorHandler.handleError(
        'Namespace validation failed - missing namespace',
        new Error('Namespace missing'),
        { category: 'namespace-checks', namespaceType: typeof this.namespace }
      );
      return validation;
    }

    for (const method of this.requiredMethods) {
      if (typeof this.namespace[method] !== 'function') {
        validation.valid = false;
        validation.issues.push(`Missing required method: ${method}`);
        this._errorHandler.handleError(
          'Required method missing',
          new Error(`Method ${method} not found`),
          { category: 'method-validation', missingMethod: method }
        );
      }
    }

    return validation;
  }

  /**
   * Runs full modal validation
   * @returns {Promise<{valid: boolean, issues: string[], canProceed: boolean, timestamp: string}>}
   */
  async validateModalFunctionality() {
    const validation = {
      valid: false,
      issues: [],
      canProceed: false,
      timestamp: new Date().toISOString()
    };

    try {
      const nsValidation = await this.validateNamespace();
      validation.issues.push(...nsValidation.issues);
      validation.valid = nsValidation.valid;
      validation.canProceed = validation.valid;

      if (!validation.valid) {
        this._errorHandler.handleError(
          'Modal functionality validation failed',
          new Error('Validation failed'),
          { category: 'validation-process', issues: validation.issues.slice(0, 3) }
        );
      }
    } catch (err) {
      validation.issues.push(`Validation error: ${err.message}`);
      this._errorHandler.handleFatalError(
        'Critical error during modal functionality validation',
        err,
        { category: 'validation-process' }
      );
    }

    return validation;
  }

  /**
   * Retrieve recent errors
   */
  getRecentErrors(minutes = 5) {
    return this._errorHandler.getRecentErrors(minutes);
  }

  /**
   * Whether any fatal errors occurred
   */
  hasCriticalIssues() {
    return this._errorHandler.hasFatalErrors();
  }

  /**
   * Retrieve error statistics
   */
  getErrorStats() {
    return this._errorHandler.getErrorStats();
  }

  /**
   * Clear recorded errors (if supported)
   */
  resetErrors() {
    if (this._errorHandler.clearErrors) {
      this._errorHandler.clearErrors();
      console.log('📊 ModalValidator errors cleared');
    }
  }

  /**
   * Get debug information for diagnostics
   */
  getDebugInfo() {
    try {
      const stats = this._errorHandler.getErrorStats();
      return {
        hasNamespace: !!this.namespace,
        requiredMethods: this.requiredMethods,
        namespaceType: typeof this.namespace,
        errorStats: {
          totalErrors: stats.totalErrors || 0,
          fatalErrors: stats.fatalErrors || 0,
          hasCriticalIssues: this.hasCriticalIssues()
        },
        recentErrorsCount: this.getRecentErrors().length
      };
    } catch {
      return { error: 'Failed to get debug info', hasNamespace: !!this.namespace };
    }
  }
}
